import Splide from '@splidejs/splide';

const WISHLIST_KEY = 'holytaste_wishlist';
let memoryWishlist = [];

const getWishlist = () => {
  try {
    const wishlist = JSON.parse(window.localStorage.getItem(WISHLIST_KEY));

    memoryWishlist = Array.isArray(wishlist)
      ? [
          ...new Set(
            wishlist.map(String).filter((id) => /^[1-9]\d*$/.test(id))
          ),
        ].slice(0, 50)
      : [];

    return memoryWishlist;
  } catch (error) {
    return memoryWishlist;
  }
};

const setWishlist = (wishlist) => {
  memoryWishlist = wishlist;

  try {
    window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(wishlist));
  } catch (error) {
    // The UI remains usable when storage is unavailable (for example, private mode).
  }
};

const syncWishlistButtons = () => {
  const wishlist = new Set(getWishlist());

  document.querySelectorAll('[data-wishlist-count]').forEach((count) => {
    count.textContent = String(wishlist.size);
    count.hidden = wishlist.size === 0;
  });

  document.querySelectorAll('[data-wishlist-product]').forEach((button) => {
    const isSaved = wishlist.has(button.dataset.wishlistProduct);

    button.classList.toggle('is-active', isSaved);
    button.setAttribute('aria-pressed', String(isSaved));
    button.setAttribute(
      'aria-label',
      isSaved
        ? button.dataset.wishlistRemoveLabel
        : button.dataset.wishlistAddLabel
    );
  });
};

const initWishlistPage = () => {
  const page = document.querySelector('[data-wishlist-page]');
  if (!page) return;

  const grid = page.querySelector('[data-wishlist-grid]');
  const empty = page.querySelector('[data-wishlist-empty]');
  const status = page.querySelector('[data-wishlist-status]');

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const refreshEmptyState = () => {
    const hasProducts = Boolean(
      grid.querySelector('.product-card:not(.is-removing)')
    );
    grid.hidden = !hasProducts;
    empty.hidden = hasProducts;
  };

  // Placeholders keep the grid's height while products load, so the page
  // doesn't jump when the cards arrive.
  const renderSkeletons = (count) => {
    grid.replaceChildren(
      ...Array.from({ length: Math.min(count, 8) }, () => {
        const skeleton = document.createElement('div');
        skeleton.className = 'wishlist-page__skeleton';
        skeleton.setAttribute('aria-hidden', 'true');
        return skeleton;
      })
    );
    grid.hidden = false;
    grid.setAttribute('aria-busy', 'true');
    empty.hidden = true;
  };

  const load = async () => {
    const ids = getWishlist();
    status.classList.remove('is-error');
    if (!ids.length) {
      grid.replaceChildren();
      status.textContent = '';
      refreshEmptyState();
      return;
    }

    status.textContent = page.dataset.loading;
    renderSkeletons(ids.length);
    const params = new URLSearchParams({ ids: ids.join(',') });
    if (page.dataset.language) params.set('language', page.dataset.language);

    try {
      const response = await fetch(`${page.dataset.endpoint}?${params}`, {
        cache: 'no-store',
      });
      if (!response.ok) throw new Error('Wishlist request failed');

      const result = await response.json();
      grid.innerHTML = result.html;
      grid.removeAttribute('aria-busy');
      setWishlist(getWishlist().map((id) => String(result.idMap?.[id] || id)));
      syncWishlistButtons();
      status.textContent = '';
      refreshEmptyState();
    } catch (error) {
      status.textContent = page.dataset.error;
      status.classList.add('is-error');
      grid.replaceChildren();
      grid.removeAttribute('aria-busy');
      grid.hidden = true;
      empty.hidden = true;
    }
  };

  page.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist-product]');
    const card = button?.closest('.product-card');
    if (!card || card.classList.contains('is-removing')) return;

    card.classList.add('is-removing');
    refreshEmptyState();

    if (reducedMotion.matches || grid.hidden) {
      card.remove();
      return;
    }

    // Matches the `.is-removing` transition in _wishlist.scss.
    window.setTimeout(() => card.remove(), 300);
  });

  window.addEventListener('storage', (event) => {
    if (event.key === WISHLIST_KEY) load();
  });

  load();
};

const initWishlist = () => {
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist-product]');

    if (!button) return;

    event.preventDefault();

    const productId = button.dataset.wishlistProduct;
    const wishlist = new Set(getWishlist());

    if (wishlist.has(productId)) {
      wishlist.delete(productId);
    } else {
      wishlist.add(productId);
    }

    setWishlist(Array.from(wishlist));
    syncWishlistButtons();
  });

  syncWishlistButtons();
  document.addEventListener('holytaste:wishlist-sync', syncWishlistButtons);
  window.addEventListener('storage', (event) => {
    if (event.key === WISHLIST_KEY) {
      syncWishlistButtons();
    }
  });
};

const initProductGallery = (product) => {
  const image = product.querySelector('[data-gallery-main]');
  const thumbs = Array.from(product.querySelectorAll('[data-gallery-thumb]'));
  const stage = product.querySelector('.single-product-gallery__stage');
  const thumbsSlider = product.querySelector('[data-gallery-thumbs]');
  let imageRequestId = 0;

  if (!image) return;

  let slider = null;

  if (stage && thumbsSlider && thumbs.length > 1) {
    const galleryNavigationHeight = 58;
    const isMobileGallery = () =>
      window.matchMedia('(max-width: 768px)').matches;
    const getGalleryHeight = () => {
      const [ratioWidth = 1, ratioHeight = 1] = window
        .getComputedStyle(stage)
        .aspectRatio.split('/')
        .map(Number);
      const height =
        stage.getBoundingClientRect().width * (ratioHeight / ratioWidth);

      return `${Math.round(height)}px`;
    };
    const getGalleryTrackHeight = () =>
      `${Math.max(
        0,
        parseInt(getGalleryHeight(), 10) - galleryNavigationHeight
      )}px`;
    const updateGalleryHeight = () => {
      if (!slider || isMobileGallery()) {
        thumbsSlider.style.height = '';

        return;
      }

      const height = getGalleryHeight();
      thumbsSlider.style.height = height;
      slider.options = { height: getGalleryTrackHeight() };
    };

    if (!isMobileGallery()) thumbsSlider.style.height = getGalleryHeight();

    slider = new Splide(thumbsSlider, {
      arrows: thumbs.length > 4,
      direction: 'ttb',
      drag: thumbs.length > 4,
      gap: 'clamp(14px, 0.94vw, 18px)',
      height: getGalleryTrackHeight(),
      pagination: false,
      perMove: 1,
      perPage: 4,
      speed: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 0
        : 450,
      breakpoints: {
        768: {
          arrows: thumbs.length > 4,
          autoWidth: false,
          direction: 'ltr',
          drag: true,
          height: 'auto',
          perPage: 4,
        },
      },
    });

    slider.mount();
    window.addEventListener('resize', updateGalleryHeight);
  }

  const setImage = (src, imageId = '') => {
    if (!src) return;

    thumbs.forEach((thumb, index) => {
      const isActive = Boolean(imageId) && thumb.dataset.imageId === imageId;

      thumb.classList.toggle('is-active', isActive);
      thumb.setAttribute('aria-pressed', String(isActive));

      if (isActive && slider) slider.go(index);
    });

    if (image.src === src || image.currentSrc === src) {
      imageRequestId += 1;
      image.dataset.currentImageId = imageId;

      if (stage) {
        stage.classList.remove('is-loading');
        stage.removeAttribute('aria-busy');
      }

      return;
    }

    const requestId = ++imageRequestId;
    const nextImage = new Image();
    const clearLoadingState = () => {
      if (requestId !== imageRequestId || !stage) return;

      stage.classList.remove('is-loading');
      stage.removeAttribute('aria-busy');
    };
    const commitImage = async () => {
      if (typeof nextImage.decode === 'function') {
        try {
          await nextImage.decode();
        } catch (error) {
          // The loaded image can still be displayed when decoding is unsupported.
        }
      }

      if (requestId !== imageRequestId) return;

      image.src = src;
      image.removeAttribute('srcset');
      image.dataset.currentImageId = imageId;
      window.requestAnimationFrame(clearLoadingState);
    };

    if (stage) {
      stage.classList.add('is-loading');
      stage.setAttribute('aria-busy', 'true');
    }

    nextImage.onload = commitImage;
    nextImage.onerror = clearLoadingState;
    nextImage.src = src;
  };

  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => {
      setImage(thumb.dataset.imageSrc, thumb.dataset.imageId);
    });
  });

  const variationForm = product.querySelector('.variations_form');

  if (!variationForm || typeof window.jQuery === 'undefined') return;

  window.jQuery(variationForm).on('found_variation', (event, variation) => {
    const variationImage = variation?.image?.full_src || variation?.image?.src;

    if (variationImage) {
      setImage(variationImage, String(variation.image_id || ''));
    }

    product.classList.add('has-selected-variation');
  });

  window.jQuery(variationForm).on('reset_data hide_variation', () => {
    setImage(image.dataset.defaultSrc, thumbs[0]?.dataset.imageId || '');
    product.classList.remove('has-selected-variation');
  });
};

const initVariationOptions = (product) => {
  product.querySelectorAll('.variations select').forEach((select) => {
    if (select.dataset.optionButtonsReady) return;

    const options = document.createElement('div');
    const attributeLabel =
      select.closest('tr')?.querySelector('label')?.textContent.trim() || '';
    options.className = 'variation-options';
    options.setAttribute('role', 'group');
    options.setAttribute('aria-label', attributeLabel);

    Array.from(select.options).forEach((option) => {
      if (!option.value) return;

      const button = document.createElement('button');
      button.className = 'variation-options__button';
      button.type = 'button';
      button.dataset.value = option.value;
      button.textContent = option.textContent;
      options.append(button);
    });

    const clearButton = document.createElement('button');
    clearButton.className = 'variation-options__clear';
    clearButton.type = 'button';
    clearButton.hidden = true;
    clearButton.setAttribute(
      'aria-label',
      `Clear ${attributeLabel || 'variation'} selection`
    );
    options.append(clearButton);

    const sync = () => {
      options
        .querySelectorAll('.variation-options__button')
        .forEach((button) => {
          const option = Array.from(select.options).find(
            (item) => item.value === button.dataset.value
          );
          const isSelected = select.value === button.dataset.value;

          button.classList.toggle('is-active', isSelected);
          button.setAttribute('aria-pressed', String(isSelected));
          button.disabled = Boolean(option?.disabled);
        });

      clearButton.hidden = !select.value;
    };

    options.addEventListener('click', (event) => {
      const button = event.target.closest('.variation-options__button');

      if (!button || button.disabled) return;

      select.value = button.dataset.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      sync();
    });

    clearButton.addEventListener('click', () => {
      select.value = '';
      select.dispatchEvent(new Event('change', { bubbles: true }));
      sync();
    });

    select.addEventListener('change', sync);
    select.insertAdjacentElement('afterend', options);
    select.dataset.optionButtonsReady = 'true';
    sync();
  });
};

const initQuantity = (product) => {
  product.querySelectorAll('.quantity').forEach((quantity) => {
    const input = quantity.querySelector('input.qty');

    if (!input || quantity.dataset.counterReady) return;

    const createButton = (operation, label, text) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `quantity__button quantity__button--${operation}`;
      button.dataset.quantityOperation = operation;
      button.setAttribute('aria-label', label);
      button.textContent = text;

      return button;
    };

    quantity.prepend(
      createButton('decrease', 'Decrease quantity', String.fromCharCode(8722))
    );
    quantity.append(createButton('increase', 'Increase quantity', '+'));
    quantity.dataset.counterReady = 'true';
  });

  product.addEventListener('click', (event) => {
    const button = event.target.closest('[data-quantity-operation]');

    if (!button) return;

    const input = button.closest('.quantity')?.querySelector('input.qty');

    if (!input) return;

    const step = Number(input.step) || 1;
    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    const current = Number(input.value) || min;
    const direction = button.dataset.quantityOperation === 'increase' ? 1 : -1;
    const next = Math.min(max, Math.max(min, current + direction * step));

    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
};

const initProductAccordion = (product) => {
  const accordion = product.querySelector('[data-product-accordion]');

  if (!accordion) return;

  const animations = new WeakMap();
  const prefersReducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const setItemState = (item, shouldOpen) => {
    const summary = item.querySelector('summary');
    const content = item.querySelector('.product-information__content');

    if (
      !summary ||
      !content ||
      prefersReducedMotion ||
      typeof item.animate !== 'function'
    ) {
      item.open = shouldOpen;
      return;
    }

    const activeState = animations.get(item);
    const startHeight = item.getBoundingClientRect().height;

    if (activeState) {
      item.style.height = `${startHeight}px`;
      activeState.animation.cancel();
      animations.delete(item);
    }

    if (shouldOpen) item.open = true;

    const endHeight = shouldOpen
      ? summary.offsetHeight + content.scrollHeight
      : summary.offsetHeight;
    item.style.height = `${startHeight}px`;
    item.style.overflow = 'hidden';

    const animation = item.animate(
      {
        height: [`${startHeight}px`, `${endHeight}px`],
      },
      {
        duration: 300,
        easing: 'ease-in-out',
      }
    );

    const animationState = {
      animation,
      targetOpen: shouldOpen,
    };

    animations.set(item, animationState);

    animation.onfinish = () => {
      if (animations.get(item) !== animationState) return;

      item.open = shouldOpen;
      item.style.height = '';
      item.style.overflow = '';
      animations.delete(item);
    };
  };

  accordion.querySelectorAll('details').forEach((item) => {
    const summary = item.querySelector('summary');

    if (!summary) return;

    summary.addEventListener('click', (event) => {
      event.preventDefault();

      const activeState = animations.get(item);
      const shouldOpen = !(activeState?.targetOpen ?? item.open);

      if (shouldOpen) {
        accordion.querySelectorAll('details').forEach((openItem) => {
          const openItemState = animations.get(openItem);
          const isOpen = openItemState?.targetOpen ?? openItem.open;

          if (openItem !== item && isOpen) setItemState(openItem, false);
        });
      }

      setItemState(item, shouldOpen);
    });
  });
};

const initSingleProduct = () => {
  initWishlist();
  initWishlistPage();

  document.querySelectorAll('[data-single-product]').forEach((product) => {
    initProductGallery(product);
    initVariationOptions(product);
    initQuantity(product);
    initProductAccordion(product);
  });
};

document.addEventListener('DOMContentLoaded', initSingleProduct);
